import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { useCreateParameter } from "@/hooks/use-parameters";
import { useToast } from "@/hooks/use-toast";
import { PARAMETER_CATEGORIES } from "@/components/parameters/shared-constants";
import {
  PageHeader,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  FormField,
  Select,
} from "@/components/ui";
import { inputClass } from "@/components/ui/form-field";

type ParameterCategory = components["schemas"]["ParameterCategory"];

export const Route = createFileRoute("/_authenticated/parameters/new")({
  component: CreateParameterPage,
});

interface FormState {
  code: string;
  name: string;
  unit: string;
  category: string;
  cas_number: string;
  description: string;
}

interface FormErrors {
  code?: string;
  name?: string;
  unit?: string;
}

function CreateParameterPage() {
  const { t } = useTranslation("parameters");
  const navigate = useNavigate();
  const toasts = useToast();
  const orgId = useAppStore((s) => s.activeOrgId);

  const mutation = useCreateParameter(orgId);

  const [form, setForm] = useState<FormState>({
    code: "",
    name: "",
    unit: "",
    category: "",
    cas_number: "",
    description: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const updateField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.code.trim())
      newErrors.code = t("messages.fieldRequired", { field: t("columns.code") });
    if (!form.name.trim())
      newErrors.name = t("messages.fieldRequired", { field: t("columns.name") });
    if (!form.unit.trim())
      newErrors.unit = t("messages.fieldRequired", { field: t("columns.unit") });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    mutation.mutate(
      {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        unit: form.unit.trim(),
        category: (form.category || undefined) as ParameterCategory | undefined,
        cas_number: form.cas_number.trim() || undefined,
        description: form.description.trim() || undefined,
      },
      {
        onSuccess: () => {
          toasts.success("parameters:messages.createSuccess");
          void navigate({
            to: "/parameters",
            search: { search: form.code.trim().toUpperCase() },
          });
        },
        onError: (err: unknown) => {
          if (err && typeof err === "object" && "status" in err && err.status === 409) {
            setErrors((prev) => ({ ...prev, code: t("messages.duplicateCode") }));
          } else {
            toasts.error("parameters:messages.createFailed");
          }
        },
      },
    );
  };

  const breadcrumbs = [{ label: t("title"), to: "/parameters" }, { label: t("createTitle") }];

  const categorySelectOptions = [
    { value: "", label: "—" },
    ...PARAMETER_CATEGORIES.map((opt) => ({
      value: opt.value,
      label: t(opt.labelKey),
    })),
  ];

  return (
    <div className="mx-auto max-w-2xl p-8">
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader title={t("createTitle")} />

      <form onSubmit={handleSubmit} className="mt-6">
        <Card>
          <CardContent className="space-y-4 p-6">
            <FormField label={t("columns.code")} required error={errors.code} hint="XX-YY format">
              <input
                type="text"
                className={inputClass}
                value={form.code}
                onChange={(e) => updateField("code", e.target.value.toUpperCase())}
              />
            </FormField>

            <FormField label={t("columns.name")} required error={errors.name}>
              <input
                type="text"
                className={inputClass}
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </FormField>

            <FormField label={t("columns.unit")} required error={errors.unit}>
              <input
                type="text"
                className={inputClass}
                value={form.unit}
                onChange={(e) => updateField("unit", e.target.value)}
              />
            </FormField>

            <FormField label={t("columns.category")}>
              <Select
                value={form.category}
                onChange={(e) => updateField("category", e.target.value)}
                options={categorySelectOptions}
              />
            </FormField>

            <FormField label={t("columns.casNumber")}>
              <input
                type="text"
                className={inputClass}
                value={form.cas_number}
                onChange={(e) => updateField("cas_number", e.target.value)}
              />
            </FormField>

            <FormField label={t("columns.description")}>
              <textarea
                className={`${inputClass} min-h-20 resize-y`}
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                rows={3}
              />
            </FormField>
          </CardContent>
        </Card>

        <div className="mt-6 flex gap-3">
          <Button type="submit" disabled={mutation.isPending}>
            {t("actions.save")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate({ to: "/parameters" })}
          >
            {t("actions.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}
